import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  ColumnHint,
  Input,
  Label,
  LoadingState,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@sitewise/ui';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useState } from 'react';


import { listAuditLog, listOrgs, type AuditLogEntry } from '../../api/admin.js';

export function AdminAuditPage() {
  const [orgFilter, setOrgFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');

  const orgsQuery = useQuery({ queryKey: ['admin-orgs'], queryFn: listOrgs });

  const audit = useInfiniteQuery({
    queryKey: ['admin-audit', orgFilter, actionFilter],
    queryFn: ({ pageParam }) =>
      listAuditLog({
        orgId: orgFilter || undefined,
        action: actionFilter || undefined,
        cursor: pageParam ?? undefined,
        limit: 50,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });

  const items: AuditLogEntry[] = audit.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every super-admin and tenant write is journaled. Rows are immutable."
      />

      <div className="mb-4 grid gap-3 rounded-lg border border-border bg-surface p-3 md:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="filter-org">Organization</Label>
          <select
            id="filter-org"
            className="h-9 w-full rounded border border-border bg-surface px-2 text-sm"
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
          >
            <option value="">All organizations</option>
            {orgsQuery.data?.orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="filter-action">Action contains</Label>
          <Input
            id="filter-action"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            placeholder="e.g. auth.login"
          />
        </div>
      </div>

      {audit.isPending ? (
        <LoadingState rows={6} />
      ) : audit.isError ? (
        <Alert variant="danger">
          <AlertDescription>Could not load audit log.</AlertDescription>
        </Alert>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <ColumnHint description="When this action was recorded.">When</ColumnHint>
                </TableHead>
                <TableHead>
                  <ColumnHint description="Who did it.">Actor</ColumnHint>
                </TableHead>
                <TableHead>
                  <ColumnHint description="The kind of change that was recorded.">Action</ColumnHint>
                </TableHead>
                <TableHead>
                  <ColumnHint description="The record the action applied to.">Entity</ColumnHint>
                </TableHead>
                <TableHead>
                  <ColumnHint description="The network address the action came from.">IP</ColumnHint>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="text-xs text-fg-muted tabular-nums">
                    {new Date(entry.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge variant={entry.actorKind === 'super_admin' ? 'primary' : 'default'}>
                      {entry.actorKind}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{entry.action}</TableCell>
                  <TableCell className="text-xs text-fg-secondary">
                    {entry.entityType ? (
                      <span>
                        <span className="text-fg-muted">{entry.entityType}</span>
                        <span className="mx-1 text-fg-muted">·</span>
                        <span className="font-mono">{entry.entityId?.slice(0, 8)}…</span>
                      </span>
                    ) : (
                      <span className="text-fg-muted">—</span>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-fg-muted">
                    {entry.ip ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {audit.hasNextPage ? (
        <div className="mt-4 flex justify-center">
          <Button
            variant="secondary"
            onClick={() => audit.fetchNextPage()}
            disabled={audit.isFetchingNextPage}
          >
            {audit.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </>
  );
}
